import React, { useState } from "react";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import moment from "moment";
import { durationText, loginText, outcomeView, runReason, runTitle, stepRows, triggerText } from "../utils/runHistoryView";

const FONT = "Inter, Poppins, sans-serif";
const NAVY = "#1E3A5F";
const TONE = {
  success: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  error: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  warning: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
  info: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF" },
  default: { color: "#344054", bg: "#F9FAFB", border: "#EAECF0" },
};
const STEP_TONE = { done: "#067647", failed: "#B42318", earlier: "#667085", none: "#98A2B3" };
const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm:ss A") : "—");

const Line = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 0.5 }}>
    <Box sx={{ color: "#667085", display: "flex", mt: "1px", "& svg": { fontSize: 16 } }}>{icon}</Box>
    <Typography sx={{ fontSize: "12.5px", color: "#344054", fontFamily: FONT, overflowWrap: "anywhere" }}>{children}</Typography>
  </Box>
);

/**
 * History -> Runs (2026-10-22): one card per automation run, newest first - when, who started it, which insurer login,
 * how long proposal / KYC / payment took, the total and how it ended. Words: utils/runHistoryView.js.
 *
 * @param runs  backend getErrorMessage `runs` (already newest first)
 */
const RunHistoryList = ({ runs = [] }) => {
  if (!runs.length) {
    return (
      <Typography sx={{ fontSize: "12.5px", color: "#667085", fontFamily: FONT }}>
        No runs recorded yet. Run details are kept for runs from 22 Oct 2026 on.
      </Typography>
    );
  }
  const total = runs.length;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {runs.map((run, i) => (
        <RunCard key={run.runId || i} run={run} number={total - i} defaultOpen={i === 0} />
      ))}
    </Box>
  );
};

/**
 * One run: a summary line always shown (number, part, outcome, started, total); click it for who started it, the login
 * and the step times. The newest run starts open, older ones closed.
 */
const RunCard = ({ run, number, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const outcome = outcomeView(run);
  const tone = TONE[outcome.tone] || TONE.default;
  const reason = runReason(run);
  return (
    <Box data-testid="run-card" sx={{ border: "1px solid #EAECF0", borderLeft: `4px solid ${tone.color}`, borderRadius: "10px", backgroundColor: "#fff" }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{ p: 1.5, cursor: "pointer", userSelect: "none", borderRadius: "10px", "&:hover": { backgroundColor: "#FAFBFC" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <PlayCircleOutlineRoundedIcon sx={{ fontSize: 18, color: NAVY }} />
          <Typography sx={{ fontWeight: 700, fontSize: "13.5px", color: "#101828", fontFamily: FONT }}>
            Run {number}
            {runTitle(run) ? ` · ${runTitle(run)}` : ""}
            {run.attempt ? ` · attempt ${run.attempt}` : ""}
          </Typography>
          <Chip
            label={outcome.label}
            size="small"
            sx={{ height: "auto", minHeight: 20, fontSize: "11px", fontWeight: 600, color: tone.color, backgroundColor: tone.bg, border: `1px solid ${tone.border}`, "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }}
          />
          <ExpandMoreRoundedIcon sx={{ ml: "auto", color: "#667085", fontSize: 20, transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }} />
        </Box>
        <Typography sx={{ fontSize: "12px", color: "#667085", fontFamily: FONT, mt: 0.25 }}>
          Started {fmt(run.startedAt)}
          {run.endedAt ? ` · ended ${moment(run.endedAt).format("hh:mm:ss A")}` : ""}
          {run.durationMs != null ? ` · total ${durationText(run.durationMs)}` : ""}
        </Typography>
        {reason && (
          <Typography sx={{ mt: 0.25, fontSize: "12px", color: "#B42318", fontFamily: FONT }}>Why it stopped - {reason}</Typography>
        )}
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Line icon={<PersonOutlineRoundedIcon />}>{triggerText(run.trigger)}</Line>
          <Line icon={<KeyRoundedIcon />}>Login used - {loginText(run.credential, run.company)}</Line>

          <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 0.75 }}>
            {[...stepRows(run), { key: "total", label: "Total", text: durationText(run.durationMs), state: "total" }].map((s) => (
              <Box
                key={s.key}
                data-testid={`run-step-${s.key}`}
                sx={{ px: 1, py: 0.75, borderRadius: "8px", backgroundColor: s.state === "total" ? "#EEF3F9" : "#F9FAFB", border: "1px solid #EAECF0", minWidth: 0 }}
              >
                <Typography sx={{ fontSize: "11px", color: "#667085", fontFamily: FONT }}>{s.label}</Typography>
                <Typography sx={{ fontSize: "13px", fontWeight: 700, fontFamily: FONT, color: s.state === "total" ? NAVY : STEP_TONE[s.state] }}>{s.text}</Typography>
                {s.at && (
                  <Typography sx={{ fontSize: "10.5px", color: "#98A2B3", fontFamily: FONT }}>
                    {s.state === "failed" ? "failed" : "done"} {moment(s.at).format("hh:mm:ss A")}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

        </Box>
      </Collapse>
    </Box>
  );
};

export default RunHistoryList;
